import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

import { supabase } from "~/integrations/supabase/client";

/**
 * Helper function to determine if a cutoff date is historical (before today)
 * Historical dates will use daily snapshot tables for faster queries
 */
const isHistoricalDate = (cutoffDate?: Date): boolean => {
  if (!cutoffDate) return false;
  return dayjs(cutoffDate).isBefore(dayjs().startOf("day"));
};

// Types for debt data
export interface DebtSummary {
  debt_type: "sales_requests" | "manual_facturas";
  total_debt: number;
  vencida: number;
  pronto_vencer: number;
  sana: number;
}

export interface SalesRequestDebt {
  id: string;
  client_id: string;
  client_name: string;
  client_rut: string;
  client_email: string;
  credit_limit: number;
  assigned_seller: string;
  factura_number: string;
  created_at: string;
  factura_emission_date: string;
  payment_due_date: string;
  original_amount: number;
  payments_before_cutoff: number;
  debt_amount_at_cutoff: number;
  days_to_due_at_cutoff: number;
  status: string;
  sales_condition_name: string;
  has_checks_in_portfolio: boolean;
  checks_in_portfolio_amount: number;
  payment_terms: number;
  siniestro: boolean;
  cobranza_judicial: boolean;
}

export interface ManualFacturaDebt {
  id: string;
  client_id: string;
  client_name: string;
  client_rut: string;
  client_email: string;
  credit_limit: number;
  assigned_seller: string;
  doc_number: string;
  created_at: string;
  payment_due_date: string;
  original_balance: number;
  payments_before_cutoff: number;
  debt_amount_at_cutoff: number;
  days_to_due_at_cutoff: number;
  siniestro: boolean;
  cobranza_judicial: boolean;
}

export interface ClientDebtDetail {
  client_id: string;
  client_name: string;
  client_rut: string;
  client_email: string;
  credit_limit: number;
  assigned_seller: string;
  total_debt: number;
  vencida: number;
  pronto_vencer: number;
  sana: number;
  credit_utilization_percent: number;
  sales_requests_debt: number;
  manual_facturas_debt: number;
}

/**
 * Helper function to fetch debt summary from RPC
 */
const fetchDebtSummaryFromRPC = async (
  cutoffDate?: Date
): Promise<DebtSummary[]> => {
  const { data, error } = await supabase.rpc(
    "get_debt_summary_at_cutoff_fixed",
    {
      cutoff_date:
        cutoffDate?.toISOString().split("T")[0] ||
        new Date().toISOString().split("T")[0],
    }
  );

  if (error) {
    console.error("Error fetching debt summary from RPC:", error);
    throw new Error(`Failed to fetch debt summary: ${error.message}`);
  }

  return data || [];
};

/**
 * Hook to fetch debt summary at a specific cutoff date
 * Uses daily_debt_summary table for historical dates (performance + audit trail)
 * Uses RPC function for current date (real-time accuracy)
 */
export const useDebtSummary = (cutoffDate?: Date) => {
  const historical = isHistoricalDate(cutoffDate);

  return useQuery({
    queryKey: [
      "debt-summary",
      cutoffDate?.toISOString(),
      historical ? "snapshot" : "rpc",
    ],
    queryFn: async (): Promise<DebtSummary[]> => {
      if (historical && cutoffDate) {
        const dateStr = cutoffDate.toISOString().split("T")[0];
        const results: DebtSummary[] = [];

        // 1. Sales requests summary from daily_debt_summary snapshot
        const { data: summaryData, error: summaryError } = await supabase
          .from("daily_debt_summary")
          .select("*")
          .eq("snapshot_date", dateStr)
          .maybeSingle();

        if (summaryError) {
          console.error(
            "Error fetching from daily_debt_summary:",
            summaryError
          );
          return fetchDebtSummaryFromRPC(cutoffDate);
        }

        if (summaryData) {
          results.push({
            debt_type: "sales_requests",
            total_debt: summaryData.total_debt,
            vencida: summaryData.vencida,
            pronto_vencer: summaryData.pronto_vencer,
            sana: summaryData.sana,
          });
        }

        // 2. Manual facturas summary — aggregate from daily_manual_factura_debt_records
        const { data: mfRecords, error: mfError } = await supabase
          .from("daily_manual_factura_debt_records")
          .select("debt_amount_at_cutoff, days_to_due_at_cutoff")
          .eq("snapshot_date", dateStr);

        if (!mfError && mfRecords && mfRecords.length > 0) {
          let mfTotal = 0;
          let mfVencida = 0;
          let mfProntoVencer = 0;
          let mfSana = 0;
          for (const r of mfRecords) {
            const amt = Number(r.debt_amount_at_cutoff || 0);
            const days = r.days_to_due_at_cutoff;
            mfTotal += amt;
            if (days === null || days < 0) {
              mfVencida += amt;
            } else if (days < 5) {
              mfProntoVencer += amt;
            } else {
              mfSana += amt;
            }
          }
          results.push({
            debt_type: "manual_facturas",
            total_debt: mfTotal,
            vencida: mfVencida,
            pronto_vencer: mfProntoVencer,
            sana: mfSana,
          });
        }

        // If we got at least one result, return it; otherwise fallback to RPC
        if (results.length > 0) return results;

        console.log(`No snapshots found for ${dateStr}, using RPC fallback`);
        return fetchDebtSummaryFromRPC(cutoffDate);
      }

      // Current date or no cutoff: use RPC for real-time data
      return fetchDebtSummaryFromRPC(cutoffDate);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Helper function to fetch sales requests debt from RPC
 */
const fetchSalesRequestsDebtFromRPC = async (
  cutoffDate?: Date
): Promise<SalesRequestDebt[]> => {
  const { data, error } = await supabase.rpc(
    "get_sales_requests_debt_at_cutoff_fixed",
    {
      cutoff_date:
        cutoffDate?.toISOString().split("T")[0] ||
        new Date().toISOString().split("T")[0],
    }
  );

  if (error) {
    console.error("Error fetching sales requests debt from RPC:", error);
    throw new Error(`Failed to fetch sales requests debt: ${error.message}`);
  }

  return data || [];
};

/**
 * Hook to fetch sales requests debt details at a specific cutoff date
 * Uses daily_debt_records table for historical dates (consistency with snapshots)
 * Uses RPC function for current date (real-time accuracy)
 */
export const useSalesRequestsDebt = (cutoffDate?: Date) => {
  const historical = isHistoricalDate(cutoffDate);

  return useQuery({
    queryKey: [
      "sales-requests-debt",
      cutoffDate?.toISOString(),
      historical ? "snapshot" : "rpc",
    ],
    queryFn: async (): Promise<SalesRequestDebt[]> => {
      if (historical && cutoffDate) {
        const dateStr = cutoffDate.toISOString().split("T")[0];
        const { data, error } = await supabase
          .from("daily_debt_records")
          .select("*")
          .eq("snapshot_date", dateStr);

        if (error) {
          console.error("Error fetching from daily_debt_records:", error);
          return fetchSalesRequestsDebtFromRPC(cutoffDate);
        }

        if (!data || data.length === 0) {
          console.log(
            `No debt records snapshot for ${dateStr}, using RPC fallback`
          );
          return fetchSalesRequestsDebtFromRPC(cutoffDate);
        }

        return data.map((row) => ({
          id: row.sales_request_id,
          client_id: row.client_id,
          client_name: row.client_name,
          client_rut: row.client_rut,
          client_email: "",
          credit_limit: 0,
          assigned_seller: row.assigned_seller,
          factura_number: row.factura_number,
          created_at: row.created_at,
          factura_emission_date: row.factura_emission_date,
          payment_due_date: row.payment_due_date,
          original_amount: row.original_amount,
          payments_before_cutoff: row.payments_before_cutoff,
          debt_amount_at_cutoff: row.debt_amount_at_cutoff,
          days_to_due_at_cutoff: row.days_to_due_at_cutoff,
          status: row.status,
          sales_condition_name: row.sales_condition_name,
          has_checks_in_portfolio: row.has_checks_in_portfolio,
          checks_in_portfolio_amount: row.checks_in_portfolio_amount,
          payment_terms: row.payment_terms,
          siniestro: row.siniestro || false,
          cobranza_judicial: row.cobranza_judicial || false,
        }));
      }

      // Current date or no cutoff: use RPC for real-time data
      return fetchSalesRequestsDebtFromRPC(cutoffDate);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Helper function to fetch manual facturas debt from RPC
 */
const fetchManualFacturasDebtFromRPC = async (
  cutoffDate?: Date
): Promise<ManualFacturaDebt[]> => {
  const { data, error } = await supabase.rpc(
    "get_manual_facturas_debt_at_cutoff",
    {
      cutoff_date:
        cutoffDate?.toISOString().split("T")[0] ||
        new Date().toISOString().split("T")[0],
    }
  );

  if (error) {
    console.error("Error fetching manual facturas debt from RPC:", error);
    throw new Error(`Failed to fetch manual facturas debt: ${error.message}`);
  }

  return data || [];
};

/**
 * Hook to fetch manual facturas debt details at a specific cutoff date
 * Uses daily_manual_factura_debt_records table for historical dates
 * Uses RPC function for current date (real-time accuracy)
 */
export const useManualFacturasDebt = (cutoffDate?: Date) => {
  const historical = isHistoricalDate(cutoffDate);

  return useQuery({
    queryKey: [
      "manual-facturas-debt",
      cutoffDate?.toISOString(),
      historical ? "snapshot" : "rpc",
    ],
    queryFn: async (): Promise<ManualFacturaDebt[]> => {
      if (historical && cutoffDate) {
        const dateStr = cutoffDate.toISOString().split("T")[0];
        const { data, error } = await supabase
          .from("daily_manual_factura_debt_records")
          .select("*")
          .eq("snapshot_date", dateStr);

        if (error) {
          console.error(
            "Error fetching from daily_manual_factura_debt_records:",
            error
          );
          return fetchManualFacturasDebtFromRPC(cutoffDate);
        }

        if (!data || data.length === 0) {
          console.log(
            `No manual factura debt snapshot for ${dateStr}, using RPC fallback`
          );
          return fetchManualFacturasDebtFromRPC(cutoffDate);
        }

        return data.map((row) => ({
          id: row.manual_factura_id,
          client_id: row.client_id,
          client_name: row.client_name,
          client_rut: row.client_rut,
          client_email: "",
          credit_limit: 0,
          assigned_seller: row.assigned_seller,
          doc_number: row.doc_number,
          created_at: row.created_at,
          payment_due_date: row.payment_due_date,
          original_balance: row.original_balance,
          payments_before_cutoff: row.payments_before_cutoff,
          debt_amount_at_cutoff: row.debt_amount_at_cutoff,
          days_to_due_at_cutoff: row.days_to_due_at_cutoff,
          siniestro: row.siniestro || false,
          cobranza_judicial: row.cobranza_judicial || false,
        }));
      }

      return fetchManualFacturasDebtFromRPC(cutoffDate);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Helper function to fetch client debt details from RPC
 */
const fetchClientDebtDetailsFromRPC = async (
  cutoffDate?: Date
): Promise<ClientDebtDetail[]> => {
  const { data, error } = await supabase.rpc(
    "get_client_debt_details_at_cutoff_fixed",
    {
      cutoff_date:
        cutoffDate?.toISOString().split("T")[0] ||
        new Date().toISOString().split("T")[0],
    }
  );

  if (error) {
    console.error("Error fetching client debt details from RPC:", error);
    throw new Error(`Failed to fetch client debt details: ${error.message}`);
  }

  return data || [];
};

/**
 * Hook to fetch client debt details aggregation at a specific cutoff date
 * Uses daily_client_debt_details table for historical dates (performance + audit trail)
 * Uses RPC function for current date (real-time accuracy)
 */
export const useClientDebtDetails = (cutoffDate?: Date) => {
  const historical = isHistoricalDate(cutoffDate);

  return useQuery({
    queryKey: [
      "client-debt-details",
      cutoffDate?.toISOString(),
      historical ? "snapshot" : "rpc",
    ],
    queryFn: async (): Promise<ClientDebtDetail[]> => {
      if (historical && cutoffDate) {
        // Query from daily_client_debt_details table for historical dates
        const dateStr = cutoffDate.toISOString().split("T")[0];
        const { data, error } = await supabase
          .from("daily_client_debt_details")
          .select("*")
          .eq("snapshot_date", dateStr);

        if (error) {
          console.error(
            "Error fetching from daily_client_debt_details:",
            error
          );
          // Fallback to RPC if snapshot query fails
          return fetchClientDebtDetailsFromRPC(cutoffDate);
        }

        if (!data || data.length === 0) {
          // No snapshot exists for this date, fallback to RPC
          console.log(
            `No client debt snapshot found for ${dateStr}, using RPC fallback`
          );
          return fetchClientDebtDetailsFromRPC(cutoffDate);
        }

        // Map to ClientDebtDetail format (now with breakdown from snapshot)
        // Note: client_email, credit_limit, assigned_seller are still not available
        return data.map((row) => ({
          client_id: row.client_id,
          client_name: row.client_name,
          client_rut: row.client_rut,
          client_email: "", // Not available in snapshot
          credit_limit: 0, // Not available in snapshot
          assigned_seller: "", // Not available in snapshot
          total_debt: row.debt_amount,
          vencida: row.vencida,
          pronto_vencer: row.pronto_vencer,
          sana: row.sana,
          credit_utilization_percent: 0, // Not available in snapshot
          sales_requests_debt: row.debt_amount,
          manual_facturas_debt: 0, // Manual facturas not in daily snapshots
        }));
      }

      // Current date or no cutoff: use RPC for real-time data
      return fetchClientDebtDetailsFromRPC(cutoffDate);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to fetch specific client's sales requests debt
 */
export const useClientSalesRequestsDebt = (
  clientId: string,
  cutoffDate?: Date
) => {
  const {
    data: allSalesRequestsDebt,
    isLoading,
    error,
  } = useSalesRequestsDebt(cutoffDate);

  const clientDebt =
    allSalesRequestsDebt?.filter(
      (debt) => debt.client_id === clientId && debt.debt_amount_at_cutoff > 0
    ) || [];

  return {
    data: clientDebt,
    isLoading,
    error,
  };
};

/**
 * Hook to fetch specific client's manual facturas debt
 */
export const useClientManualFacturasDebt = (
  clientId: string,
  cutoffDate?: Date
) => {
  const {
    data: allManualFacturasDebt,
    isLoading,
    error,
  } = useManualFacturasDebt(cutoffDate);

  const clientDebt =
    allManualFacturasDebt?.filter(
      (debt) => debt.client_id === clientId && debt.debt_amount_at_cutoff > 0
    ) || [];

  return {
    data: clientDebt,
    isLoading,
    error,
  };
};

/**
 * Hook to invalidate all debt queries (useful after data changes)
 */
export const useInvalidateDebtQueries = () => {
  const queryClient = useQueryClient();

  const invalidateAllDebtQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["debt-summary"] });
    queryClient.invalidateQueries({ queryKey: ["sales-requests-debt"] });
    queryClient.invalidateQueries({ queryKey: ["manual-facturas-debt"] });
    queryClient.invalidateQueries({ queryKey: ["client-debt-details"] });
  };

  const invalidateDebtQueriesForDate = (cutoffDate?: Date) => {
    const dateKey = cutoffDate?.toISOString();
    queryClient.invalidateQueries({ queryKey: ["debt-summary", dateKey] });
    queryClient.invalidateQueries({
      queryKey: ["sales-requests-debt", dateKey],
    });
    queryClient.invalidateQueries({
      queryKey: ["manual-facturas-debt", dateKey],
    });
    queryClient.invalidateQueries({
      queryKey: ["client-debt-details", dateKey],
    });
  };

  return {
    invalidateAllDebtQueries,
    invalidateDebtQueriesForDate,
  };
};
